import {
  GameUIAction,
  ConsumeRoll,
  MoveForward,
  RollResult,
} from '@models/game-ui-actions';
import { PlayResponse } from '@models/play-response';
import ActionMap from '@services/core/ActionMap';
import { Singleton } from '@utils/singleton';

// roll action is always the first action in the queue
// finish action is always the last action in the queue
function toActionQueue(response: PlayResponse): GameUIAction[] {
  const rollAction = response.actions.find(
    (action) => action.type === 'roll_result',
  );
  const finishAction = response.actions.find(
    (action) => action.type === 'finish',
  );
  const restActions = response.actions.filter(
    (action) => action.type !== 'finish' && action.type !== 'roll_result',
  );
  const consumeRollAction: ConsumeRoll = {
    type: 'consume_roll',
    nextRoll: response.nextRoll,
    rollsAvailable: response.rollsAvailable,
  };

  // When a move_forward is present we need to split movement into two pieces:
  //   1 — dice roll moves the player to the boost tile (move_forward.position)
  //   2 — move_forward animates the bonus step(s) to the real final position
  // Without this split, roll_result would jump straight to the boosted position
  // and move_forward would fire on a tile the player already passed.
  const moveForward = restActions.find((a) => a.type === 'move_forward') as
    | MoveForward
    | undefined;

  let adjustedRollAction = rollAction;
  if (moveForward && rollAction) {
    // Redirect roll_result to stop at the boost tile, not the final position
    adjustedRollAction = {
      ...(rollAction as RollResult),
      position: String(moveForward.position),
    };
    // Inject the real final position so the move_forward handler can finish
    // the journey after playing the boost animation
    moveForward.finalPosition = Number((rollAction as RollResult).position);
  }

  return [
    adjustedRollAction,
    consumeRollAction,
    ...restActions,
    finishAction,
  ].filter((a) => !!a);
}

// EventEngine - dispatches game actions through the registered ActionMap
@Singleton
export default class EventEngine {
  private actionMap: ActionMap;

  constructor() {
    this.actionMap = new ActionMap();
  }

  async execute(action: GameUIAction): Promise<void> {
    await this.actionMap.execute(action.type, action);
  }

  async executeResponse(response: PlayResponse): Promise<void> {
    const queue = toActionQueue(response);
    for (const action of queue) {
      await this.execute(action);
    }
  }

  setActionMap(actionMap: ActionMap): void {
    this.actionMap = actionMap;
  }

  getActionMap(): ActionMap {
    return this.actionMap;
  }

  clear(): void {
    this.actionMap.clear();
  }
}
